import clsx from "clsx";
import { ArrowUpRightIcon, Bookmark, EditIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/Button/Button";
import { SmartMatchIcon } from "@/components/Icons/Icons";
import { AppText } from "@/components/Text/AppText";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { TooltipContent } from "@/components/Tooltip/TooltipContent";
import { TooltipTrigger } from "@/components/Tooltip/TooltipTrigger";
import { Input } from "@/components/form/Input";
import { cleanUrl } from "@/utils/string";

export const TableCell = ({
    children,
    center = false,
    className,
}: {
    children: React.ReactNode;
    center?: boolean;
    className?: string;
}) => {
    return (
        <td
            className={clsx(
                "h-[68px] whitespace-normal p-3 align-middle",
                center && "text-center",
                className,
            )}
        >
            {children}
        </td>
    );
};

export const TableHeaderCell = ({
    value,
    className,
}: {
    value: string;
    className?: string;
}) => {
    return (
        <th className={clsx(className, "bg-primary-700 text-center")}>
            <AppText variant="labelsbuttons">{value}</AppText>
        </th>
    );
};

export const JobTitleCell = ({
    title,
    organization,
    organizationLogo,
    isLast,
    width = "w-[200px]",
}: {
    title: string;
    organization: string;
    organizationLogo: string | null;
    isLast: boolean;
    width?: string;
}) => (
    <TableCell className={clsx("flex flex-col", isLast && "rounded-bl-lg")}>
        <div className="flex h-full flex-col justify-center">
            <AppText
                variant="labelsbuttons"
                className={clsx(
                    "overflow-hidden text-ellipsis whitespace-nowrap text-neutral-900",
                    width,
                )}
            >
                {title}
            </AppText>
            <div className="flex items-center gap-2">
                {!!organizationLogo && (
                    <img
                        src={organizationLogo}
                        alt={organization}
                        className="h-6 w-6 object-contain"
                    />
                )}
                {!!organization && (
                    <AppText
                        variant="footnote"
                        className="overflow-hidden text-ellipsis whitespace-nowrap !font-medium text-neutral-700"
                    >
                        {organization}
                    </AppText>
                )}
            </div>
        </div>
    </TableCell>
);

export const JobSourceCell = ({
    source,
    url,
}: {
    source: string;
    url: string;
}) => (
    <TableCell center>
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 text-neutral-900 hover:text-neutral-700"
        >
            <AppText
                variant="footnote"
                className="max-w-[150px] truncate"
            >
                {source}
            </AppText>
            <ArrowUpRightIcon className="h-4 w-4 flex-shrink-0" />
        </a>
    </TableCell>
);

export const ActionButton = ({
    icon,
    onClick,
    gradient = false,
    isLoading = false,
}: {
    icon: React.ReactNode;
    onClick: () => void;
    gradient?: boolean;
    isLoading?: boolean;
}) => {
    return (
        <button
            className={clsx(
                "hover:text-neutral-600",
                gradient && "rounded-md p-0.5 ring-2 ring-primary-500",
                isLoading && "cursor-wait",
            )}
            onClick={onClick}
            disabled={isLoading}
        >
            {icon}
        </button>
    );
};

export const JobActionsCell = ({
    isLoadingActions,
    onSaveJob,
    onOpenSmartMatchModal,
    isSaved,
}: {
    isLoadingActions: boolean;
    onSaveJob: () => void;
    onOpenSmartMatchModal: () => void;
    isSaved: boolean;
}) => (
    <TableCell>
        <div className={clsx("flex items-center gap-3", "justify-around")}>
            <Button
                variant="outline"
                color="gradient"
                size="small"
                className="!h-8 whitespace-nowrap"
                onClick={onOpenSmartMatchModal}
                disabled={isLoadingActions}
                leftIcon={
                    <SmartMatchIcon
                        gradient
                        className="h-7 w-7"
                    />
                }
            >
                Tailor Resume
            </Button>
            <ActionButton
                icon={
                    isSaved ? (
                        <Bookmark
                            size={20}
                            className="text-primary-200"
                            style={{
                                fill: "currentColor",
                            }}
                        />
                    ) : (
                        <Bookmark
                            size={20}
                            className="text-neutral-900"
                        />
                    )
                }
                onClick={onSaveJob}
                isLoading={isLoadingActions}
            />
        </div>
    </TableCell>
);

export const EditableInputValue = ({
    initialValue = "",
    onChange,
    readOnly = false,
}: {
    initialValue?: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(initialValue);
    const [isValidUrl, setIsValidUrl] = useState(true);

    const getIsValidUrl = (url: string) => {
        if (!url) {
            return true;
        }
        try {
            new URL(url);
            return true;
        } catch (error) {
            return false;
        }
    };

    const persistUrl = () => {
        const isValid = getIsValidUrl(value);
        setIsValidUrl(isValid);
        if (isValid) {
            onChange(value);
        }
    };

    useEffect(() => {
        setIsValidUrl(getIsValidUrl(value));
    }, [value]);

    return readOnly ? (
        <Button
            color="grey"
            variant="text"
            className="w-[170px] overflow-hidden text-neutral-700"
            to={value}
        >
            {value ? `${cleanUrl(value).substring(0, 20)}...` : "-"}
        </Button>
    ) : (
        <Tooltip showOnCreate={!isValidUrl && !isEditing}>
            <TooltipTrigger>
                <Input
                    value={isEditing ? value : cleanUrl(value)}
                    onChange={(_, value) => setValue(value)}
                    className={clsx("w-full", !isValidUrl && "!ring-error-500")}
                    iconClassName="!cursor-pointer !pointer-events-auto"
                    trailingIcon={
                        isEditing ? (
                            <EditIcon size={16} />
                        ) : initialValue ? (
                            <ArrowUpRightIcon
                                size={16}
                                onClick={() => {
                                    window.open(value, "_blank");
                                }}
                            />
                        ) : (
                            <EditIcon
                                size={16}
                                opacity={0.5}
                            />
                        )
                    }
                    onBlur={() => {
                        setIsEditing(false);
                        persistUrl();
                    }}
                    onFocus={() => setIsEditing(true)}
                    placeholder="Enter link here..."
                />
            </TooltipTrigger>
            {!isValidUrl && (
                <TooltipContent>
                    <AppText variant="footnote">Invalid URL</AppText>
                </TooltipContent>
            )}
        </Tooltip>
    );
};
